import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Fab, Typography, Button } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "@/shared/hooks";
import { useVideoEngine } from "./useVideoEngine";
import { usePlayerHotkeys } from "./usePlayerHotkeys";
import { useIdle } from "./useIdle";
import { PlayerFooter } from "./PlayerFooter";
import { VideoAnnotationsOverlay } from "./VideoAnnotationsOverlay";
import { useCast } from "./cast/useCast";
import { CastOverlay } from "./cast/CastOverlay";
import type { PlayerTimecode } from "./playerTimecode";
import { renderPlayerOverlay, type PlayerOverlay } from "./renderPlayerOverlay";

export interface PlayerProps {
  src?: string | null;
  poster?: string | null;
  /** Content title shown (with a short delay) in the pause overlay. */
  title?: string | null;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  hotkeys?: boolean;
  startPosition?: number | null;
  /** CMCD contentId (Cloudflare streamCMCDAdaptiveGroups): прокидывается в
   * useVideoEngine, только на экранах с реальным контентом. */
  cmcdContentId?: string | null;
  fill?: boolean;
  /** Плоский ReactNode рендерится всегда безусловно (как CloseRouteDialogButton).
   * Render-prop получает состояние плеера: controlsVisible (элементы,
   * которые прячутся вместе с футером, см. PromoButtonWidget), currentTime
   * и isCasting (живой интерактив прячется во время Chromecast). */
  overlay?: PlayerOverlay;
  pauseOverlay?: ReactNode;
  onTimeUpdate?: (video: HTMLVideoElement) => void;
  onPause?: (video: HTMLVideoElement) => void;
  onEnded?: (video: HTMLVideoElement) => void;
  onEngineError?: () => void;
  /** Кнопка «Далее» автоперехода к следующему видео (референсный
   * nextPlaylistContentButton): рендерится внутри контейнера плеера,
   * получает текущее состояние видео и видимость контролов, чтобы
   * подниматься над футером, пока тот показан. */
  nextButton?: (
    state: { currentTime: number; duration: number; ended: boolean },
    controlsVisible: boolean,
  ) => ReactNode;
  /** Метки таймлайна: MARKER — иконки на сикбаре (+тумблер в футере),
   * ANNOTATION — всплывающие карточки поверх видео. */
  timecodes?: PlayerTimecode[] | null;
  getVideoElement?: (video: HTMLVideoElement | null) => void;
  /** Called on any user gesture that expresses intent to start playback
   * (clicking the video area or the big play button), even before `src` is
   * available. Lets the caller gate session/view creation on this instead of
   * firing it unconditionally on mount regardless of `autoPlay`. */
  onPlayIntent?: () => void;
  /** True for a currently-live stream — see PlayerFooter's isLive prop. */
  isLive?: boolean;
  /** «Разобраться с помощником» (Task 15) под текстом ошибки воспроизведения.
   * Player живёт в shared и не знает про features/assistant, рендер
   * передаёт вызывающая сторона (ContentPlayer). errorDetails: что сломалось
   * (тип и детали hls.js, HTTP-код), помощник получает это с первым ходом. */
  renderPlaybackErrorAction?: (errorDetails: string | null) => ReactNode;
}

export const Player: FC<PlayerProps> = ({
  src,
  poster,
  title,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  hotkeys = true,
  startPosition,
  cmcdContentId,
  fill = false,
  overlay,
  pauseOverlay,
  onTimeUpdate,
  onPause,
  onEnded,
  onEngineError,
  nextButton,
  timecodes,
  getVideoElement,
  onPlayIntent,
  isLive,
  renderPlaybackErrorAction,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const { videoRef, state, actions } = useVideoEngine({
    src,
    autoPlay,
    muted,
    loop,
    startPosition,
    cmcdContentId,
  });
  const { idle, wake } = useIdle(5000);
  const controlsVisible =
    controls && (!idle || state.paused || state.isLoading);

  const cast = useCast({
    src,
    poster,
    title,
    isLive,
    currentTime: state.currentTime,
  });

  // Пока идёт каст этого же ролика, локальный <video> должен молчать — иначе
  // звук играет и с телефона/компьютера, и с телевизора одновременно.
  // Элемент остаётся смонтированным (hls.js его не теряет), просто
  // визуально накрывается CastOverlay ниже. Когда каст заканчивается
  // (сессия закрыта, разорвана или доиграла до конца), подхватываем
  // локальное воспроизведение с той позиции, где остановился приёмник,
  // а не с той, на которой локальное видео стояло на паузе всё это время.
  const wasCastingRef = useRef(false);
  useEffect(() => {
    if (cast.isCastingThis) {
      actions.pause();
    } else if (wasCastingRef.current) {
      actions.seek(cast.remote.currentTime);
      if (!cast.remote.paused) void actions.play();
    }
    wasCastingRef.current = cast.isCastingThis;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cast.isCastingThis]);

  // Пока идёт каст, футер плеера (play/pause, сикбар, громкость) управляет
  // не локальным видео, а удалённым плеером на приёмнике — тот же
  // компонент, другой источник состояния/экшенов, как в легаси
  // (CastVideoScreen/CastLiveScreen поверх общего PlayerFooter).
  const footerState = cast.isCastingThis
    ? {
        currentTime: cast.remote.currentTime,
        duration: cast.remote.duration,
        paused: cast.remote.paused,
        ended: false,
        isLoading: cast.remote.isLoading,
        buffered: cast.remote.duration,
        volume: cast.remote.volume,
        muted: cast.remote.muted,
        playbackRate: 1,
        levels: [],
        currentLevel: -1,
        autoLevel: true,
        error: false,
        errorDetails: null,
        started: true,
        liveEdge: null,
        liveTargetDuration: null,
      }
    : state;

  const footerActions = cast.isCastingThis
    ? {
        play: async () => cast.playOrPause(),
        pause: () => cast.playOrPause(),
        togglePlay: () => cast.playOrPause(),
        seek: (time: number) => cast.seek(time),
        // Ресивер по умолчанию не поддерживает относительную перемотку,
        // переключение качества/скорости и повторную загрузку — эти
        // элементы футера при касте либо скрыты, либо безвредны как no-op.
        seekBy: () => undefined,
        setVolume: (volume: number) => cast.setVolume(volume),
        toggleMute: () => cast.toggleMute(),
        setPlaybackRate: () => undefined,
        setLevel: () => undefined,
        retry: () => undefined,
        goLive: () => undefined,
      }
    : actions;

  // Start (or restart) the auto-hide countdown as soon as playback starts, so
  // controls hide after 5s even without any prior mouse/touch activity.
  useEffect(() => {
    if (!state.paused) wake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.paused]);

  // The whole pause overlay (backdrop + Fab + title) fades in together, 2s
  // after pausing — matches reference PlayerPause, which gates backdrop
  // opacity, inner opacity and title on the single `visible` flag set by one
  // SHOW_TIMEOUT(2000) timer, instead of popping in immediately.
  const [pauseOverlayVisible, setPauseOverlayVisible] = useState(false);
  useEffect(() => {
    if (!state.paused) {
      setPauseOverlayVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setPauseOverlayVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [state.paused]);

  useEffect(() => {
    getVideoElement?.(videoRef.current);
    return () => getVideoElement?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const handleTime = () => onTimeUpdate?.(video);
    const handlePause = () => onPause?.(video);
    const handleEnded = () => onEnded?.(video);
    video.addEventListener("timeupdate", handleTime);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    return () => {
      video.removeEventListener("timeupdate", handleTime);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, onTimeUpdate, onPause, onEnded]);

  useEffect(() => {
    if (state.error) onEngineError?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (container.requestFullscreen) {
      void container.requestFullscreen();
    } else {
      // iOS Safari fallback
      const video = container.querySelector("video") as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void;
      };
      video?.webkitEnterFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const togglePip = useCallback(async () => {
    const video = containerRef.current?.querySelector("video");
    if (!video) return;
    try {
      if (document.pictureInPictureElement)
        await document.exitPictureInPicture();
      else await video.requestPictureInPicture();
    } catch {
      // PiP unsupported
    }
  }, []);

  usePlayerHotkeys(footerState, footerActions, {
    enabled: hotkeys,
    onToggleFullscreen: toggleFullscreen,
  });

  return (
    <Box
      ref={containerRef}
      onMouseMove={wake}
      onTouchStart={wake}
      sx={{
        position: "relative",
        width: "100%",
        ...(fill ? { height: "100%" } : { aspectRatio: "16 / 9" }),
        backgroundColor: "#000",
        overflow: "hidden",
        cursor: controlsVisible ? "default" : "none",
      }}
    >
      <video
        ref={videoRef}
        playsInline
        poster={poster ?? undefined}
        style={{ width: "100%", height: "100%" }}
        onClick={() => {
          onPlayIntent?.();
          actions.togglePlay();
        }}
        onDoubleClick={toggleFullscreen}
      />
      {cast.isCastingThis && (
        <CastOverlay deviceName={cast.deviceName} poster={poster} />
      )}
      {state.isLoading && !state.error && !cast.isCastingThis && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      )}
      {controls &&
        state.paused &&
        !state.isLoading &&
        !state.error &&
        !cast.isCastingThis && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: pauseOverlayVisible
                ? "rgba(0,0,0,0.6)"
                : "rgba(0,0,0,0)",
              transition: "background-color 0.3s ease",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
                opacity: pauseOverlayVisible ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
            >
              <Fab
                color="primary"
                onClick={() => {
                  onPlayIntent?.();
                  actions.togglePlay();
                }}
                sx={{ pointerEvents: "auto", width: 72, height: 72 }}
                aria-label={t("player.controls.play")}
              >
                <PlayArrowIcon sx={{ fontSize: 48 }} />
              </Fab>
              {/* Skip the built-in title when the caller supplies its own
                pauseOverlay (e.g. embed's clickable title-link) to avoid
                rendering the title twice. */}
              {title && !pauseOverlay && (
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    textAlign: "center",
                    px: 2,
                    fontSize: { xs: "0.7rem", sm: "1.3rem", md: "1.6rem" },
                  }}
                >
                  {title}
                </Typography>
              )}
              {pauseOverlay}
            </Box>
          </Box>
        )}
      {state.error && !cast.isCastingThis && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          <Typography variant="body2">{t("player.playbackError")}</Typography>
          <Button
            startIcon={<ReplayIcon />}
            onClick={actions.retry}
            sx={{ color: "#fff" }}
          >
            {t("player.retry")}
          </Button>
          {renderPlaybackErrorAction?.(state.errorDetails)}
        </Box>
      )}
      {/* Оба завязаны на локальный currentTime/ended, которые во время
          каста заморожены (видео на паузе) — во время каста на другом
          экране показывать их нечего, как и в легаси. */}
      {!cast.isCastingThis && (
        <VideoAnnotationsOverlay
          timecodes={timecodes}
          currentTime={state.currentTime}
          paused={state.paused}
          resetKey={src}
        />
      )}
      {renderPlayerOverlay(overlay, {
        controlsVisible,
        currentTime: cast.isCastingThis
          ? cast.remote.currentTime
          : state.currentTime,
        isCasting: cast.isCastingThis,
      })}
      {!cast.isCastingThis &&
        nextButton?.(
          {
            currentTime: state.currentTime,
            duration: state.duration,
            ended: state.ended,
          },
          controlsVisible,
        )}
      {controls && (
        <PlayerFooter
          src={src}
          state={footerState}
          actions={footerActions}
          visible={controlsVisible}
          fullscreen={fullscreen}
          onToggleFullscreen={toggleFullscreen}
          onTogglePip={togglePip}
          timecodes={timecodes}
          isLive={isLive}
          castAvailable={cast.isAvailable}
          casting={cast.isCastingThis}
          onCast={cast.start}
        />
      )}
    </Box>
  );
};
